import React, { cloneElement, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import ButtonSecondary from '../ui/ButtonSecondary';
import AutocompleteSearchBar from '../AutocompleteSearchBar';
import BaseTable from '../BaseTable';
import CheckMark from '../CheckMark';
import SendInvite from '../SendInvite';
import EmployeeFullName from '../EmployeeFullName';

import api from '../../api';
import { sortText } from '../../utils/sortHelper';
import './index.css';
import { fetchAllEmployees, addOrRemoveUser } from '../../store/actions';


const JobEmployeesBlock = (props) => {
  const [localEmployees, setLocalEmployees] = useState([]);
  const [employeesToAddList, setEmployeesToAddList] = useState([]);
  const [employeesSort, setEmployeesSort] = useState({isDesc: false, prop: ''})
  const [employeeToAdd, setEmployeeToAdd] = useState(null);
  
  useEffect(() => {
    !props.employeeList.length && props.fetchAllEmployees();
    !localEmployees.length && api.getUsersForAGivenJob(props.jobId).then(res => setLocalEmployees(res.data.data));
  }, []);

  useEffect(() => {
    const employeesToAddList = props.employeeList.map(emp => {
      return {
        ...emp,
        dataLabel: emp.firstName + ' ' + emp.lastName,
        toLowerCase: function() { return this.dataLabel.toLowerCase() }
      }
    }).filter(emp => emp.status === 'active');
    const filteredEmployeesToAddList = employeesToAddList.filter(emp => {
      return !localEmployees.find(localEmp => {
        return localEmp.UUID === emp.UUID
      });
    });
    setEmployeesToAddList(filteredEmployeesToAddList);
  }, [props.employeeList, localEmployees]);

  useEffect(() => {
    const {isDesc, prop} = employeesSort;
    let sorted;
    if (prop === 'name') {
      sorted = [...localEmployees].sort((a, b) => {
        const nameA = getText(a);
        const nameB = getText(b);
        if (nameA === nameB) {
          return 0;
        } else if (nameA > nameB) {
          return isDesc ? 1 : -1;
        } else {
          return isDesc ? -1 : 1;
        }
      });
    } else if (prop === 'scheduledStartTime') {
      sorted = [...localEmployees].sort((a, b) => {
        const diff = moment(a[prop], 'YYYY-MM-DD HH:mm:ss') - moment(b[prop], 'YYYY-MM-DD HH:mm:ss');
        return isDesc ? diff : diff * -1;
      });
    } else {
      sorted = [...localEmployees].sort(sortText(prop, isDesc))
    }
    setLocalEmployees(sorted);
  }, [employeesSort])

  const sortTable = ({dataKey}) => {
    setEmployeesSort({
      isDesc: employeesSort.prop === dataKey ? !employeesSort.isDesc : false,
      prop: dataKey
    });
  }

  const selectOption = (prop, option) => {
    setEmployeeToAdd(option);
  }

  const getText = (employee) => {
    return employee ? `${employee.firstName} ${employee.lastName}` : '';
  }

  const actions = {
    delete: (empToDelete) => {
      props.addOrRemoveUser(props.jobId, -1);
      setLocalEmployees([...localEmployees].filter(emp => empToDelete.UUID !== emp.UUID))
      setEmployeesToAddList([...employeesToAddList, empToDelete]);
      api.removeUserFromJob(props.jobId, empToDelete.UUID);
    }    
  }

  const addEmployee = () => {
    const newLocalEmployees = [...localEmployees];
    newLocalEmployees.push(employeeToAdd);
    setEmployeesToAddList(employeesToAddList.filter(emp => emp.UUID !== employeeToAdd.UUID));
    setLocalEmployees(newLocalEmployees);
    setEmployeeToAdd(null);
    props.addOrRemoveUser(props.jobId, 1);
    api.assignUserForJob(props.jobId, employeeToAdd.UUID);
  }

  const confirmUserToJob = (user) => {
    api.confirmEmployeeToJob(user, { UUID: props.jobId });
    setLocalEmployees(localEmployees.map(emp => {
      return emp.UUID === user.UUID ? {...emp, confirmedTime: true} : emp;
    }))
  }

  const sendUserInviteForJob = (user) => {
    api.sendUserInviteForJob(user, { UUID: props.jobId });
    setLocalEmployees(localEmployees.map(emp => {
      return emp.UUID === user.UUID ? {...emp, invitedTime: 'Invite sent'} : emp;
    }))
  }

  const sendAllUsersInviteForJob = (user) => {
    api.sendAllUsersInviteForJob(localEmployees.map(emp => emp.UUID), { UUID: props.jobId });
    setLocalEmployees(localEmployees.map(emp => {
      return {...emp, invitedTime: 'Invite sent'};
    }))
  }

  const tableOptions = {
    columns: [
      { 
        name: "Confirmed",
        dataKey: 'isConfirmed',
        type: 'component',
        component: cloneElement(<CheckMark />, { onClick: confirmUserToJob }),
        isSortable: false,
      },
      { 
        name: "Name",
        dataKey: 'name',
        type: 'component',
        component: cloneElement(<EmployeeFullName />, { toggleEmployeeModal: props.toggleEmployeeModal }),
        isSortable: true,
      },
      { 
        name: "Phone",
        dataKey: 'phone',
        type: 'text',
        isSortable: true,
      },
      { 
        name: "Invite",
        type: 'component',
        component: cloneElement(<SendInvite />, { onClick: sendUserInviteForJob }),
        isSortable: false
      },
      { 
        name: "Remove",
        type: 'delete',
        component: 'Delete',
        isSortable: false,
        flexGrow: 'fg25-c',
      },
    ]
  }

  return (
    <div className="job-employees">
      <h3 className="job-employees__title">Employees in the list ({localEmployees.length})</h3>
      <div className="job-employees__header">
        <AutocompleteSearchBar
          inputButtonPlaceholder="Add Employee" 
          className="job-employees__search"
          selectOptions={{options: employeesToAddList, prop: 'name'}}
          inputPlaceholder="Search Employee"
          value={getText(employeeToAdd)}
          selectOption={selectOption}
          getText={getText}
        />
        <ButtonSecondary name="Add" onClick={addEmployee} disabled={!employeeToAdd}/>
        <ButtonSecondary name="Send invites to all" onClick={sendAllUsersInviteForJob}/>
      </div>
      <BaseTable onSort={sortTable} options={tableOptions} data={localEmployees} actions={actions}/>
    </div>
  );
}

const mapStateToProps = ({ employees: { employeeList } }) => ({ employeeList });

export default connect(mapStateToProps, { fetchAllEmployees, addOrRemoveUser })(JobEmployeesBlock);
